import {
	ContextGetRequestApi,
	ContextGetResponse,
	ContextGetResponseApi,
	ContextList,
	ContextListRequest,
	ContextListResponse,
	ServiceRequest,
} from '@/types/conductor'
import type {AxiosInstance, AxiosResponse} from 'axios'
import axios from 'axios'
import {validate} from '@/utils/validator.util'
import {
	ContextGetRequestApiSchema,
	ContextGetResponseApiSchema,
	ContextListRequestSchema,
	ContextListResponseSchema,
} from '@/validators/conductor.validator'
import {buildServiceRequest} from '@/utils/service.util'
import {getAccessToken} from '@/api/authorisation'
import {getApiHost, getProtocol} from '@/utils/conductor.util'
import {useProfilesStore} from '@/store/modules/userProfile'
import router from '@/router'

// ---- Base request ID ----
let _requestId = 100

function allocateRequestId(): number {
	return ++_requestId
}

// ---- Dynamic client agent string ----
const VERSION = '2.1'

function getClientAgentString(): string {

	const currentPath = router.currentRoute.value.path

	let client = 'share-web'
	if (currentPath.startsWith('/odds')) {
		client = 'share-odds'
	} else if (currentPath.startsWith('/bet/')) {
		client = 'share-direct'
	} else if (currentPath.startsWith('/betpic')) {
		client = 'share-betpic'
	}

	// Get user info from Pinia store
	const userProfileStore = useProfilesStore()
	const userId = userProfileStore?.profile?.userId ?? 0
	const username = userProfileStore?.profile?.username ?? 'Guest'

	return `client=${client};version=${VERSION};userId=${userId};username=${username}`
}

// ---- Base request data ----
function getBaseRequestData() {
	return {
		requestId: allocateRequestId(),
		clientAgentString: getClientAgentString(),
	}
}

// ---- Create Axios instance dynamically ----
export function createConductorInstance(): AxiosInstance {
	const conductor = axios.create()

	conductor.interceptors.request.use((config) => {
		const accessToken: string = getAccessToken()
		const protocol: string = getProtocol()
		const apiHost: string = getApiHost()

		config.baseURL = `${protocol}://${apiHost}/bet-composer/composer/conductor`
		config.headers.Authorization = accessToken

		return config
	})

	conductor.interceptors.response.use(
			(response) => response,
			(error) => {
				console.error(error)
				return Promise.reject(error)
			},
	)

	return conductor
}

const conductor = createConductorInstance()

// ---- API functions ----
export async function getContextList(): Promise<ContextList> {
	const contextListRequest: ContextListRequest = {
		contextListRequest: {
			...getBaseRequestData(),
		},
	}
	validate(ContextListRequestSchema, contextListRequest)

	const response: AxiosResponse = await conductor.post('/global', contextListRequest)
	const contextListResponse: ContextListResponse = response.data
	validate(ContextListResponseSchema, contextListResponse)

	return contextListResponse.contextListResponse
}

export async function getContextGetResponse(context: string): Promise<ContextGetResponse> {
	const serviceRequest: ServiceRequest = buildServiceRequest()
	const contextGetRequestApi: ContextGetRequestApi = {
		contextGetRequest: {
			...serviceRequest,
		},
	}
	validate(ContextGetRequestApiSchema, contextGetRequestApi)

	const response: AxiosResponse = await conductor.post(`/context/${context}`, contextGetRequestApi)
	const contextGetResponseApi: ContextGetResponseApi = response.data
	validate(ContextGetResponseApiSchema, contextGetResponseApi)

	return contextGetResponseApi.contextGetResponse
}